import { WEIGHT_UNITS, type WeightUnit } from "@babia/checks";
import { useEffect, useRef, useState } from "react";
import {
  NOTE_KIND_EMOJI,
  NOTE_KIND_LABEL,
  NOTE_KIND_TITLE,
  NOTE_KINDS,
  type NoteKind,
  inUnit,
  parseWeightInput,
  type WeightInput,
} from "../../../lib/entry-text.ts";
import { cn } from "../../../lib/utils.ts";
import { Button } from "../../ui/button.tsx";
import { Textarea } from "../../ui/input.tsx";
import { Sheet } from "../../ui/sheet.tsx";

const PLACEHOLDER: Record<NoteKind, string> = {
  general: "Spat up a little after the 2oz…",
  bath: "Optional — loved the warm water…",
  weight: "Optional — at the 2-month checkup…",
  poop: "Mustard yellow and seedy…",
  pee: "Darker than usual this morning…",
  feeding: "Fussy for the first ounce, then fine…",
  sleep: "Only settled once swaddled…",
  health: "Temp 99.1°F, a little congested…",
};

/** The weight unit chips: pounds with ounces, or kilograms. */
const WEIGHT_CHIP: Record<WeightUnit, string> = { lb: "lb oz", kg: "kg" };

const NUMBER_INPUT =
  "heading w-[96px] bg-transparent text-center text-[44px] leading-none tracking-[-0.04em] tabular-nums outline-offset-4";

const noWeight = (unit: WeightUnit): WeightInput => ({
  unit,
  pounds: "",
  ounces: "",
  kilograms: "",
});

/**
 * A kind of note (a bath, a weight, a poop…) and its text; a bath needs no
 * words, and neither does a weight with its number.
 */
export function NoteSheet({
  open,
  babyName,
  weightUnit,
  weightHint,
  onClose,
  onSave,
}: {
  open: boolean;
  babyName: string;
  /** The household's unit, where the weight's unit chips start. */
  weightUnit: WeightUnit;
  /** Why a weight logged now would get no percentile, and where to fix it. */
  weightHint: string | null;
  onClose: () => void;
  /** `grams` is the weight's number, for a weight note that has one. */
  onSave: (kind: NoteKind, text: string, grams: number | null) => void;
}) {
  const [kind, setKind] = useState<NoteKind>("general");
  const [text, setText] = useState("");
  const [weight, setWeight] = useState<WeightInput>(noWeight(weightUnit));
  useEffect(() => {
    if (open) {
      setKind("general");
      setText("");
      setWeight(noWeight(weightUnit));
    }
    // Only on opening: a household reload while the sheet is open keeps what was typed.
  }, [open]);
  // The household's unit can arrive after the sheet opened (a cold start):
  // follow it until a number is typed.
  const typed = !!(weight.pounds.trim() || weight.ounces.trim() || weight.kilograms.trim());
  useEffect(() => {
    if (!typed) setWeight((w) => ({ ...w, unit: weightUnit }));
  }, [weightUnit]);
  const trimmed = text.trim();
  const weighing = kind === "weight";
  const parsed = parseWeightInput(weight);
  const grams = weighing && "grams" in parsed ? parsed.grams : null;
  const invalid = weighing && "invalid" in parsed ? parsed.invalid : null;
  const canSave = weighing
    ? !invalid && (grams !== null || !!trimmed)
    : !!trimmed || kind === "bath";

  return (
    <Sheet open={open} title={`Note — ${babyName}`} emoji={NOTE_KIND_EMOJI[kind]} onClose={onClose}>
      <NoteFields
        kind={kind}
        onKind={setKind}
        text={text}
        onText={setText}
        weight={weight}
        onWeight={setWeight}
        weightHint={weightHint}
      />
      <Button
        variant="primary"
        className="mt-3 w-full"
        disabled={!canSave}
        onClick={() => onSave(kind, trimmed, grams)}
      >
        Save {kind === "general" ? "note" : NOTE_KIND_TITLE[kind].toLowerCase()}
      </Button>
    </Sheet>
  );
}

/**
 * The note's kind chips, a weight's number in either unit, and the text; the
 * note sheet and the edit sheet share them. With `lockedKind` (a weight note
 * that has its number) the other kinds can't be picked.
 */
export function NoteFields({
  kind,
  onKind,
  text,
  onText,
  weight,
  onWeight,
  weightHint,
  lockedKind,
  autoFocusText = true,
}: {
  kind: NoteKind;
  onKind: (kind: NoteKind) => void;
  text: string;
  onText: (text: string) => void;
  weight: WeightInput;
  onWeight: (weight: WeightInput) => void;
  weightHint: string | null;
  lockedKind?: NoteKind;
  /** The note sheet opens on the text; the edit sheet opens without a keyboard. */
  autoFocusText?: boolean;
}) {
  const firstNumber = useRef<HTMLInputElement>(null);
  const weighing = kind === "weight";
  const parsed = parseWeightInput(weight);
  const invalid = weighing && "invalid" in parsed ? parsed.invalid : null;
  const set = (field: keyof Omit<WeightInput, "unit">) => (value: string) =>
    onWeight({ ...weight, [field]: value });

  const pick = (k: NoteKind) => {
    onKind(k);
    // The number is what a weight is for: start there.
    if (k === "weight") requestAnimationFrame(() => firstNumber.current?.focus());
  };

  return (
    <>
      <div
        role="group"
        aria-label="What the note is about"
        className="mt-3.5 grid grid-cols-4 gap-2"
      >
        {NOTE_KINDS.map((k) => (
          <Button
            key={k}
            variant="chip"
            className={cn("flex-col gap-0.5 py-1.5 text-[14px]", kind === k && "is-selected")}
            aria-pressed={kind === k}
            disabled={lockedKind !== undefined && k !== lockedKind}
            onClick={() => pick(k)}
          >
            <span aria-hidden="true" className="text-[18px] leading-none">
              {NOTE_KIND_EMOJI[k]}
            </span>
            {NOTE_KIND_LABEL[k]}
          </Button>
        ))}
      </div>
      {weighing ? (
        <div className="mt-3.5">
          <div className="flex items-end justify-center gap-4">
            {weight.unit === "lb" ? (
              <>
                <label className="text-center">
                  <input
                    ref={firstNumber}
                    className={NUMBER_INPUT}
                    value={weight.pounds}
                    onChange={(e) => set("pounds")(e.target.value)}
                    inputMode="numeric"
                    placeholder="0"
                    aria-label="Pounds"
                    aria-invalid={!!invalid}
                  />
                  <span className="kicker mt-1 block text-[12px]">lb</span>
                </label>
                <label className="text-center">
                  <input
                    className={NUMBER_INPUT}
                    value={weight.ounces}
                    onChange={(e) => set("ounces")(e.target.value)}
                    inputMode="decimal"
                    placeholder="0"
                    aria-label="Ounces"
                    aria-invalid={!!invalid}
                  />
                  <span className="kicker mt-1 block text-[12px]">oz</span>
                </label>
              </>
            ) : (
              <label className="text-center">
                <input
                  ref={firstNumber}
                  className={cn(NUMBER_INPUT, "w-[150px]")}
                  value={weight.kilograms}
                  onChange={(e) => set("kilograms")(e.target.value)}
                  inputMode="decimal"
                  placeholder="0"
                  aria-label="Kilograms"
                  aria-invalid={!!invalid}
                />
                <span className="kicker mt-1 block text-[12px]">kg</span>
              </label>
            )}
          </div>
          <div role="group" aria-label="Weight unit" className="mt-3 flex justify-center gap-1.5">
            {WEIGHT_UNITS.map((u) => (
              <Button
                key={u}
                variant="chip"
                className={cn("text-[14px]", weight.unit === u && "is-selected")}
                aria-pressed={weight.unit === u}
                onClick={() => onWeight(inUnit(weight, u))}
              >
                {WEIGHT_CHIP[u]}
              </Button>
            ))}
          </div>
          {invalid ? (
            <p className="mt-2 text-center text-[13px] text-accent-2" role="status">
              {invalid}
            </p>
          ) : weightHint ? (
            <p className="mt-2 text-center text-[13px] opacity-65">{weightHint}</p>
          ) : null}
        </div>
      ) : null}
      <Textarea
        className="mt-3"
        rows={weighing ? 2 : 3}
        value={text}
        onChange={(e) => onText(e.target.value)}
        placeholder={PLACEHOLDER[kind]}
        aria-label={`${NOTE_KIND_TITLE[kind]} text`}
        autoFocus={autoFocusText}
      />
    </>
  );
}
